/**
    * @description      : Modern Admin Dashboard with Amazing UI/UX
    * @author           : USER
    * @group            :
    * @created          : 04/02/2026 - 14:00:00
    *
    * MODIFICATION LOG
    * - Version         : 3.0.0
    * - Date            : 04/02/2026
    * - Author          : Claude
    * - Modification    : Complete redesign with modern UI/UX, glassmorphism, and animations
**/
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AdminDashboard from './AdminDashboard';
import AdminDocuments from './AdminDocuments';
import AdminAnalytics from './AdminAnalytics';
import AdminSettings from './AdminSettings';
import './AdminPageNew.css';

const AdminPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [user] = useState({ name: 'Admin User', role: 'Super Administrator', avatar: null });

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Modern Icon Components
  const DashboardIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="9" rx="1"/>
      <rect x="14" y="3" width="7" height="5" rx="1"/>
      <rect x="14" y="12" width="7" height="9" rx="1"/>
      <rect x="3" y="16" width="7" height="5" rx="1"/>
    </svg>
  );

  const DocumentsIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <line x1="10" y1="9" x2="8" y2="9"/>
    </svg>
  );

  const UsersIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  );

  const AnalyticsIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    </svg>
  );

  const SettingsIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M12 1v6M12 17v6M4.22 4.22l4.24 4.24M15.54 15.54l4.24 4.24M1 12h6M17 12h6M4.22 19.78l4.24-4.24M15.54 8.46l4.24-4.24"/>
    </svg>
  );

  const MenuIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="6" x2="21" y2="6"/>
      <line x1="3" y1="12" x2="21" y2="12"/>
      <line x1="3" y1="18" x2="21" y2="18"/>
    </svg>
  );

  const BellIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21c-.13.14-.27.26-.41.36a2 2 0 0 1-2.64 0c-.14-.1-.28-.22-.41-.36"/>
    </svg>
  );

  const SearchIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/>
      <path d="M21 21l-4.35-4.35"/>
    </svg>
  );

  const BackIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5"/>
      <path d="M12 19l-7-7 7-7"/>
    </svg>
  );

  const navigationItems = [
    { id: 'dashboard', label: 'Dashboard', icon: DashboardIcon, description: 'Overview & Analytics' },
    { id: 'documents', label: 'Documents', icon: DocumentsIcon, description: 'Content Management' },
    { id: 'users', label: 'Users', icon: UsersIcon, description: 'User Management' },
    { id: 'analytics', label: 'Analytics', icon: AnalyticsIcon, description: 'Deep Insights' },
    { id: 'settings', label: 'Settings', icon: SettingsIcon, description: 'System Configuration' }
  ];

  const sidebarVariants = {
    expanded: { width: '280px' },
    collapsed: { width: '80px' }
  };

  const contentVariants = {
    expanded: { marginLeft: '280px' },
    collapsed: { marginLeft: '80px' }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 }
  };

  return (
    <div className="admin-container">
      {/* Modern Sidebar */}
      <motion.aside 
        className={`modern-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}
        variants={sidebarVariants}
        animate={sidebarCollapsed ? 'collapsed' : 'expanded'}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      >
        {/* Sidebar Header */}
        <div className="sidebar-header">
          <motion.div 
            className="brand-section"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="brand-icon">
              <div className="brand-sparkle">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <defs>
                    <linearGradient id="brand-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#4285f4" />
                      <stop offset="50%" stopColor="#9b72cb" />
                      <stop offset="100%" stopColor="#d96570" />
                    </linearGradient>
                  </defs>
                  <path d="M16 2L19 11L28 14L19 17L16 26L13 17L4 14L13 11L16 2Z" fill="url(#brand-gradient)" />
                </svg>
              </div>
            </div>
            <AnimatePresence>
              {!sidebarCollapsed && (
                <motion.div 
                  className="brand-text"
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <h2>Tuliza Admin</h2>
                  <span>Tuliza Control Center</span>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
          
          <motion.button 
            className="sidebar-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <MenuIcon />
          </motion.button>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {/* Back to Chat Button */}
          <div className="nav-section back-section">
            <motion.button
              className="nav-item back-button"
              onClick={() => navigate('/chat')}
              whileHover={{ x: 4, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <BackIcon />
              <AnimatePresence>
                {!sidebarCollapsed && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    Back to Chat
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
          </div>

          <div className="nav-section">
            <AnimatePresence>
              {!sidebarCollapsed && (
                <motion.div 
                  className="nav-title"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  Main Navigation
                </motion.div>
              )}
            </AnimatePresence>
            
            {navigationItems.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <motion.button
                  key={item.id}
                  className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(item.id)}
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ x: 4 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="nav-icon">
                    <IconComponent />
                  </div>
                  <AnimatePresence>
                    {!sidebarCollapsed && (
                      <motion.div 
                        className="nav-content"
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <span className="nav-label">{item.label}</span>
                        <span className="nav-description">{item.description}</span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>
              );
            })}
          </div>
        </nav>

        {/* User Profile */}
        <div className="sidebar-footer">
          <div className="user-profile">
            <div className="user-avatar">
              <div className="avatar-placeholder">
                {user.name.split(' ').map(n => n[0]).join('')}
              </div>
              <div className="status-indicator online"></div>
            </div>
            <AnimatePresence>
              {!sidebarCollapsed && (
                <motion.div 
                  className="user-info"
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="user-name">{user.name}</div>
                  <div className="user-role">{user.role}</div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.aside>

      {/* Main Content */}
      <motion.main 
        className="admin-main"
        variants={contentVariants}
        animate={sidebarCollapsed ? 'collapsed' : 'expanded'}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      >
        {/* Top Header */}
        <header className="admin-header">
          <div className="header-left">
            <motion.div 
              className="page-title"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1>{navigationItems.find(item => item.id === activeTab)?.label}</h1>
              <span className="page-subtitle">{navigationItems.find(item => item.id === activeTab)?.description}</span>
            </motion.div>
          </div>
          
          <div className="header-right">
            <div className="header-actions">
              <motion.div 
                className="search-box"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                <SearchIcon />
                <input type="text" placeholder="Search anything..." />
              </motion.div>
              
              <motion.button 
                className="notification-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <BellIcon />
                <span className="notification-badge">3</span>
              </motion.button>
            </div>
            
            <div className="time-display">
              <div className="current-time">
                {currentTime.toLocaleTimeString()}
              </div>
              <div className="current-date">
                {currentTime.toLocaleDateString()}
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <div className="admin-content">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {activeTab === 'dashboard' && <AdminDashboard />}
              {activeTab === 'documents' && <AdminDocuments />}
              {activeTab === 'users' && <UserManagement />}
              {activeTab === 'analytics' && <AdminAnalytics />}
              {activeTab === 'settings' && <AdminSettings />}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.main>
    </div>
  );
};

// Additional Components for new sections
const UserManagement = () => (
  <div className="content-section">
    <div className="section-header">
      <h2>User Management</h2>
      <p>Manage users, roles, and permissions</p>
    </div>
    <div className="feature-coming-soon">
      <div className="coming-soon-icon">
        <UsersIcon />
      </div>
      <h3>User Management Dashboard</h3>
      <p>Advanced user management features coming soon!</p>
    </div>
  </div>
);

const Analytics = () => (
  <div className="content-section">
    <div className="section-header">
      <h2>Advanced Analytics</h2>
      <p>Deep insights and performance metrics</p>
    </div>
    <div className="feature-coming-soon">
      <div className="coming-soon-icon">
        <AnalyticsIcon />
      </div>
      <h3>Analytics Dashboard</h3>
      <p>Comprehensive analytics and reporting tools coming soon!</p>
    </div>
  </div>
);

const Settings = () => (
  <div className="content-section">
    <div className="section-header">
      <h2>System Settings</h2>
      <p>Configure system preferences and options</p>
    </div>
    <div className="feature-coming-soon">
      <div className="coming-soon-icon">
        <SettingsIcon />
      </div>
      <h3>Settings Panel</h3>
      <p>Advanced system configuration options coming soon!</p>
    </div>
  </div>
);

// Helper icon components
const UsersIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const AnalyticsIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
  </svg>
);

const SettingsIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M12 1v6M12 17v6M4.22 4.22l4.24 4.24M15.54 15.54l4.24 4.24M1 12h6M17 12h6M4.22 19.78l4.24-4.24M15.54 8.46l4.24-4.24"/>
  </svg>
);

export default AdminPage;
